import { useCallback, useEffect, useRef } from 'react'

import { useSearch } from '@tanstack/react-router'
import Cookies from 'js-cookie'

import { getSharedCookieDomain } from '@/lib/cookies'

// Ceiling, not a guarantee: WebKit's ITP caps any cookie written from
// document.cookie at 7 days, so Safari attribution is short regardless.
const ATTRIBUTION_WINDOW_DAYS = 90

// The affiliate program promises a 30-day last-click window (SIG-2504). The
// cookie's own expiry IS the window — the backend never re-checks click age.
const AFF_ATTRIBUTION_WINDOW_DAYS = 30

const VISITOR_ID_TTL_DAYS = 365

// Column width of AffiliateClick.LandingPage on the API side.
const MAX_LANDING_PAGE_LENGTH = 500

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// crypto.randomUUID is unavailable in non-secure contexts and some embedded
// webviews. Falls through two levels: getRandomValues (still cryptographic,
// just without the native UUID formatter) and finally Math.random (not
// cryptographically strong, but this id is a visitor correlation key, not a
// secret). Always returns a syntactically valid v4, since the backend drops
// anything that fails UUID validation silently.
const generateUuid = (): string => {
  try {
    return crypto.randomUUID()
  } catch {
    // fall through
  }

  try {
    const bytes = crypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, '0'),
    ).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  } catch {
    // fall through
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0
    const value = char === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

// Fire-and-forget: clicks are informational only (money never derives from
// them), so a lost or failed beacon must never surface anywhere.
const sendAffiliateClickBeacon = (code: string, visitorId: string) => {
  try {
    // Trailing slash stripped like the site does (`tracking-cookies.ts:152`):
    // a `VITE_API_URL` ending in `/` would post to `//v1/public/...` and lose
    // every click to a 404 the `.catch` below would swallow.
    const apiUrl = String(import.meta.env.VITE_API_URL).replace(/\/+$/, '')
    fetch(`${apiUrl}/v1/public/affiliation/clicks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        // Origin + pathname, never `href`: the same rule the site states for
        // itself (`Tracking.astro:80-84`) — a query string carries `?aff=`,
        // every UTM, `gclid` and sometimes personal data, and `LandingPage`
        // has to mean the same thing whichever writer filled it.
        page: (window.location.origin + window.location.pathname).slice(
          0,
          MAX_LANDING_PAGE_LENGTH,
        ),
        visitorId,
      }),
      keepalive: true,
    }).catch(() => {})
  } catch {
    // Some embedded webviews expose no global fetch; attribution still works
    // through the cookie, only the click stat is lost.
  }
}

// Referrer is the only value we shorten here, and by dropping the path rather
// than cutting the string: the column is 200 chars and document.referrer is the
// browser's string, not ours to keep short. Every other field is capped at the
// payload boundary in lib/tracking, where a cut cannot produce a lone surrogate
// that encodeURIComponent would reject.
const MAX_REFERRER_LENGTH = 200

const getCookieDomain = (): string => {
  const hostname = window.location.hostname
  return getSharedCookieDomain(hostname) ?? hostname
}

const isOwnHost = (hostname: string): boolean =>
  hostname === window.location.hostname ||
  hostname === 'signater.com' ||
  hostname.endsWith('.signater.com')

const getExternalDocumentReferrer = (): string | undefined => {
  const documentReferrer = document.referrer
  if (!documentReferrer) return undefined

  try {
    const referrerUrl = new URL(documentReferrer)
    // Schemes without an authority (file:, mailto:, blob:) parse fine and leave
    // host empty. Their "referrer" is a local path or an address — never an
    // acquisition source, and first-touch would freeze it for the whole window.
    if (!referrerUrl.host) return undefined
    if (isOwnHost(referrerUrl.hostname)) return undefined

    // Referrer query strings carry noise and sometimes personal data, so keep
    // scheme + host + path, and drop the path when even that would overflow.
    // Built from the parts because non-http referrers (android-app://, common
    // on Gmail and LinkedIn) have an opaque origin that serializes to the
    // literal string "null". Must match Tracking.astro on the site, which
    // writes the same shared cookie.
    const base = `${referrerUrl.protocol}//${referrerUrl.host}`
    const value = base + referrerUrl.pathname
    return value.length <= MAX_REFERRER_LENGTH ? value : base
  } catch {
    return undefined
  }
}

const normalizeSearchKeys = (
  search: Record<string, unknown>,
): Record<string, string | undefined> => {
  const normalized: Record<string, string | undefined> = {}

  for (const [key, value] of Object.entries(search)) {
    if (typeof value === 'string' || typeof value === 'number') {
      normalized[key.toLowerCase()] = String(value)
    }
  }

  // The router parses search values through JSON.parse, which rounds the long
  // numeric ad ids Meta puts in utm_content to the nearest double. Raw URL
  // values win so the app writes the same shared cookie Tracking.astro does.
  // The router object still seeds this map, because a memory history (tests)
  // leaves window.location.search empty.
  for (const [key, value] of new URLSearchParams(window.location.search)) {
    normalized[key.toLowerCase()] = value
  }

  return normalized
}

export const useTrackingCookies = () => {
  const searchParams = useSearch({ strict: false })

  // StrictMode double-invokes effects on mount in dev, and this ref (unlike a
  // module-level or component-state guard) survives that double-invoke as the
  // same instance while still resetting per real mount. Keyed by aff + the raw
  // search string so a genuinely new ?aff= navigation still fires — only the
  // synchronous re-run with identical params is suppressed.
  const beaconSentForRef = useRef<string | null>(null)

  const setCookie = useCallback(
    (name: string, value: string, expiresInDays = ATTRIBUTION_WINDOW_DAYS) => {
      if (!value) return

      // SameSite=None requires Secure, and Secure is dropped over plain http, so
      // a dev server on localhost would store nothing at all. Mirrors the same
      // fallback in Tracking.astro on the site.
      const isHttps = window.location.protocol === 'https:'

      Cookies.set(name, value, {
        domain: getCookieDomain(),
        path: '/',
        secure: isHttps,
        sameSite: isHttps ? 'None' : 'Lax',
        expires: expiresInDays,
      })
    },
    [],
  )

  // Kept for whoever sent the visitor first: overwriting hands the sale to
  // whichever channel they happened to come back through last.
  const setFirstTouchCookie = useCallback(
    (name: string, value: string) => {
      if (!value || Cookies.get(name)) return
      setCookie(name, value)
    },
    [setCookie],
  )

  // Wrapped end-to-end (not just the id generation): a Cookies.get/set failure
  // here must never break the rest of the tracking effect below it. generateUuid
  // itself is guaranteed not to throw, but this is the single choke point that
  // keeps that guarantee from mattering — the effect never needs its own guard.
  const ensureVisitorId = useCallback((): string => {
    try {
      const existing = Cookies.get('TrackingVisitorId')
      if (existing && UUID_PATTERN.test(existing)) return existing

      const visitorId = generateUuid()
      setCookie('TrackingVisitorId', visitorId, VISITOR_ID_TTL_DAYS)
      return visitorId
    } catch {
      // Attribution still works through the other cookies; only the visitor
      // id is ephemeral for this render (not persisted).
      return generateUuid()
    }
  }, [setCookie])

  const syncFacebookCookies = useCallback(() => {
    const fbp = Cookies.get('_fbp')
    const fbc = Cookies.get('_fbc')
    const trackingFbp = Cookies.get('TrackingFbp')
    const trackingFbc = Cookies.get('TrackingFbc')

    // Sync _fbp to TrackingFbp if _fbp exists and either TrackingFbp doesn't exist or they are different
    if (fbp && (!trackingFbp || fbp !== trackingFbp)) {
      setCookie('TrackingFbp', fbp)
    }

    // Sync _fbc to TrackingFbc if _fbc exists and either TrackingFbc doesn't exist or they are different
    if (fbc && (!trackingFbc || fbc !== trackingFbc)) {
      setCookie('TrackingFbc', fbc)
    }
  }, [setCookie])

  useEffect(() => {
    // Synchronize Facebook cookies
    syncFacebookCookies()

    // Set up an interval to check periodically
    const intervalId = setInterval(syncFacebookCookies, 5000) // Check every 5 seconds

    return () => {
      clearInterval(intervalId)
    }
  }, [syncFacebookCookies])

  useEffect(() => {
    const visitDate = new Date().toISOString()

    if (!Cookies.get('TrackingGeneratedAtUtc')) {
      setCookie('TrackingGeneratedAtUtc', visitDate)
    }

    const {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      referrer,
      ref,
      via,
      aff,
      cta,
      feature,
      recurrence,
      envelopes,
      gclid,
      fbclid,
      device,
      matchtype,
      network,
      adposition,
    } = normalizeSearchKeys(searchParams)

    if (utm_source) setCookie('TrackingUtmSource', utm_source)
    if (utm_medium) setCookie('TrackingUtmMedium', utm_medium)
    if (utm_campaign) setCookie('TrackingUtmCampaign', utm_campaign)
    if (utm_term) setCookie('TrackingUtmTerm', utm_term)
    if (utm_content) setCookie('TrackingUtmContent', utm_content)
    if (via) setCookie('TrackingVia', via)
    if (feature) setCookie('TrackingFeature', feature)
    if (recurrence) setCookie('TrackingRecurrence', recurrence)
    // Volume the visitor picked on the site's pricing page. Deliberately NOT in
    // getTrackingCookie(): the registration payload is a backend DTO and this
    // ticket adds no field to it — the cookie exists only to position the plans
    // page after the /register hop.
    if (envelopes) setCookie('TrackingEnvelopes', envelopes)

    if (cta) {
      setCookie('TrackingCta', cta)
      // The plan CTAs used to send this slug as ?ref=, so a returning visitor
      // can carry a TrackingRef holding a page name. Now that ref is
      // first-touch, that leftover would block a real partner for the whole
      // window — drop it once, and only when it is provably the old CTA value.
      if (Cookies.get('TrackingRef') === cta)
        Cookies.remove('TrackingRef', {
          domain: getCookieDomain(),
          path: '/',
        })
    }
    if (gclid) setCookie('TrackingGclid', gclid)
    if (device) setCookie('TrackingGAdsDevice', device)
    if (matchtype) setCookie('TrackingGAdsMatchType', matchtype)
    if (network) setCookie('TrackingGAdsNetwork', network)
    if (adposition) setCookie('TrackingGAdsAdPosition', adposition)

    if (ref) setFirstTouchCookie('TrackingRef', ref)

    // Last-touch on purpose: the newest affiliate link wins the 30-day window.
    if (aff) {
      setCookie('TrackingAff', aff, AFF_ATTRIBUTION_WINDOW_DAYS)
      const visitorId = ensureVisitorId()

      const beaconKey = `${aff}::${window.location.search}`
      if (beaconSentForRef.current !== beaconKey) {
        beaconSentForRef.current = beaconKey
        sendAffiliateClickBeacon(aff, visitorId)
      }
    }

    if (referrer) {
      setCookie('TrackingReferrer', referrer)
    } else {
      const documentReferrer = getExternalDocumentReferrer()
      if (documentReferrer) {
        setFirstTouchCookie('TrackingReferrer', documentReferrer)
      }
    }

    // Meta Pixel derives _fbc from ?fbclid as fb.1.{timestamp}.{fbclid}. Seed
    // TrackingFbc so attribution survives when the Pixel hasn't run yet (user
    // hits app.signater.com straight from an ad) — but only when _fbc does not
    // already describe THIS click, since the Pixel's timestamp is the authentic
    // one and syncFacebookCookies would keep restoring it anyway.
    const pixelFbc = Cookies.get('_fbc')
    if (fbclid && pixelFbc?.split('.').pop() !== fbclid) {
      setCookie('TrackingFbc', `fb.1.${Date.now()}.${fbclid}`)
    }
  }, [searchParams, setCookie, setFirstTouchCookie, ensureVisitorId])
}
